import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip, Modal, Input, Button, Dropdown } from "antd";
import {
  MailOutlined,
  WhatsAppOutlined,
  MessageOutlined,
  CheckOutlined,
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  GiftOutlined,
  CrownOutlined,
  BarsOutlined,
  CommentOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { deleteOrder } from "../../services/orderService";
import usePermissions from "../../hooks/usePermissions";
import moment from "moment";
import { placeClickToCall } from "../../utils/clickToCall";
import { useAddCustomerFollowUp } from "../../hooks/useCustomerFollowUp";
import { useFollowUpOptions } from "../../hooks/useManageEnquiry";

// ── Follow-up status custom select (color-coded) ────────────────────────────
const statusColorME = (s) => {
  const m = {
    "No Response": "#9ca3af",
    Interested: "#16a34a",
    "Not Interested": "#6b7280",
    "Meeting Scheduled": "#0284c7",
    Converted: "#15803d",
    Closed: "#6b7280",
    "Follow Up": "#667eea",
    "Not Responding": "#9ca3af",
    "Hot Lead": "#ef4444",
    "Mark for Future": "#f59e0b",
    Negative: "#dc2626",
  };
  return m[s] || "#667eea";
};

const FollowUpStatusSelectME = ({
  value,
  onChange,
  followUpOptionsList = [],
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const selected = followUpOptionsList.find((o) => o.option_value === value);
  const color = statusColorME(selected?.label || "");
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        style={{
          width: "100%",
          padding: "9px 36px 9px 12px",
          border: `1.5px solid ${value ? color : "#fde68a"}`,
          borderRadius: "9px",
          fontSize: "13px",
          fontWeight: 700,
          color: value ? color : "#6b7280",
          background: value ? `${color}10` : "white",
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          boxSizing: "border-box",
          position: "relative",
        }}
      >
        <span style={{ flex: 1 }}>
          {selected?.label || "— Select Status —"}
        </span>
        <span
          style={{
            position: "absolute",
            right: "11px",
            top: "50%",
            transform: `translateY(-50%) rotate(${open ? "180deg" : "0deg"})`,
            transition: "transform 0.2s",
            color: value ? color : "#9ca3af",
            pointerEvents: "none",
            fontSize: "10px",
          }}
        >
          ▼
        </span>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            zIndex: 9999,
            background: "white",
            border: "1.5px solid #e8ecff",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(102,126,234,0.18)",
            overflow: "hidden",
          }}
        >
          <div
            style={{ maxHeight: "220px", overflowY: "auto", padding: "6px" }}
          >
            {followUpOptionsList.map((opt) => {
              const c = statusColorME(opt.label);
              const isActive = opt.option_value === value;
              return (
                <button
                  key={opt.option_value}
                  type="button"
                  onClick={() => {
                    onChange(opt.option_value);
                    setOpen(false);
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? "white" : c,
                    background: isActive ? c : "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: "9px",
                    transition: "all 0.15s",
                    marginBottom: "2px",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = `${c}15`;
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {opt.label}
                  {isActive && (
                    <span style={{ marginLeft: "auto", fontSize: "12px" }}>
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Column definitions (lifecycle stages) ──────────────────────────────────
const COLUMNS = [
  {
    key: "free_trial",
    title: "Active Free Trial",
    icon: <GiftOutlined />,
    color: "#0369a1",
    bg: "#e0f2fe",
    border: "#bae6fd",
    dot: "#0ea5e9",
  },
  {
    key: "pending",
    title: "Pending Payment",
    icon: <ClockCircleOutlined />,
    color: "#d97706",
    bg: "#fef3c7",
    border: "#fde68a",
    dot: "#f59e0b",
  },
  {
    key: "paid",
    title: "Active Paid",
    icon: <CrownOutlined />,
    color: "#16a34a",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    dot: "#22c55e",
  },
  {
    key: "expiring_soon",
    title: "Expiring Soon",
    icon: <ExclamationCircleOutlined />,
    color: "#ea580c",
    bg: "#fff7ed",
    border: "#fed7aa",
    dot: "#f97316",
  },
  {
    key: "expired",
    title: "Paid Expired",
    icon: <CloseCircleOutlined />,
    color: "#dc2626",
    bg: "#fef2f2",
    border: "#fecaca",
    dot: "#ef4444",
  },
  {
    key: "free_trial_expired",
    title: "Free Trial Expired",
    icon: <CloseCircleOutlined />,
    color: "#7c3aed",
    bg: "#f5f3ff",
    border: "#ddd6fe",
    dot: "#8b5cf6",
  },
  {
    key: "payment_rejected",
    title: "Payment Rejected",
    icon: <CloseCircleOutlined />,
    color: "#b91c1c",
    bg: "#fff1f2",
    border: "#fecdd3",
    dot: "#f43f5e",
  },
];

// ── Parse DD/MM/YYYY date string safely ────────────────────────────────────
const parseEndDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split("/");
  if (parts.length === 3)
    return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
  return new Date(dateStr);
};

// ── Classify each order into a lifecycle stage ─────────────────────────────
const classifyOrder = (item) => {
  // _col is set by fetchKanban (server-driven, one fetch per column)
  if (item?._col) return item._col;

  // Fallback for table view / old data
  const isFree =
    item?.customer_type === "trial" ||
    item?.plan_type === "Free Trial" ||
    (item?.payment_link === null && item?.subscription_type === "free_trial");

  if (item.subscription_end_date) {
    const endDate = parseEndDate(item.subscription_end_date);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const daysLeft = Math.floor((endDate - now) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return "expired";
    if (!isFree && daysLeft <= 7) return "expiring_soon";
  }

  if (isFree) return "free_trial";

  const isPaid =
    item?.last_payment_status === 1 || item?.last_payment_status === "1" ||
    item?.payment_status === 1 || item?.payment_status === "1";
  if (!isPaid) return "pending";

  return "paid";
};

const fmt = (n) =>
  Number(n ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const getDaysText = (item) => {
  let days;
  if (item.days_remaining !== undefined && item.days_remaining !== null) {
    days = Number(item.days_remaining);
  } else if (item.subscription_end_date) {
    const endDate = parseEndDate(item.subscription_end_date);
    const now = new Date();
    days = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
  } else {
    return null;
  }
  if (days < 0) return { text: `${Math.abs(days)}d overdue`, color: "#dc2626" };
  if (days === 0) return { text: "Expires today", color: "#ea580c" };
  if (days <= 7) return { text: `${days}d remaining`, color: "#d97706" };
  return { text: `${days}d remaining`, color: "#16a34a" };
};

// ── Shared icon button styles ──────────────────────────────────────────────
const iconBtnStyle = {
  width: "24px",
  height: "24px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "5px",
  border: "1px solid",
  cursor: "pointer",
  fontSize: "11px",
  transition: "all 0.15s",
  padding: 0,
  background: "transparent",
};

const disabledIconStyle = {
  ...iconBtnStyle,
  background: "#f5f5f5",
  color: "#bfbfbf",
  borderColor: "#e5e5e5",
  cursor: "not-allowed",
};

// ═══════════════════════════════════════════════════════════════════════════
// KanbanCard
// ═══════════════════════════════════════════════════════════════════════════
const KanbanCard = ({
  item,
  colDef,
  onPaymentModal,
  onDelete,
  planMap = {},
  onCallLog,
}) => {
  const { canEdit, canDelete } = usePermissions(
    "/cms/manage-customer/manage-order",
  );
  const navigate = useNavigate();
  const daysInfo = getDaysText(item);
  const isFree = item?.payment_link?.toLowerCase().includes("free");
  const isPaid = item?.customer_type === "paid";

  return (
    <div
      style={{
        background: "white",
        borderRadius: "12px",
        border: `1px solid ${colDef.border}`,
        padding: "16px",
        marginBottom: "10px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.2s, transform 0.15s",
        cursor: "default",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.1)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* Header: Avatar + Name + Amount */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
          marginBottom: "12px",
        }}
      >
        <div
          onClick={() => navigate(`/cms/manageUserData/user-profile/${item.user_id}`)}
          style={{ textDecoration: "none", flexShrink: 0, cursor: "pointer" }}
        >
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${colDef.color}, ${colDef.dot})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "13px",
              fontWeight: 700,
            }}
          >
            {item.customer_name
              ?.split(" ")
              .map((w) => w.charAt(0))
              .join("")
              .slice(0, 2)
              .toUpperCase() || "?"}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            onClick={() => navigate(`/cms/manageUserData/user-profile/${item.user_id}`)}
            style={{
              cursor: "pointer",
              fontWeight: 700,
              fontSize: "14px",
              color: "#1f2937",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.customer_name || "Unknown"}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#6b7280",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.customer_email || "No email"}
          </div>
        </div>
        <div
          style={{
            fontWeight: 800,
            fontSize: "15px",
            color: colDef.color,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          ₹{fmt(
            (colDef.key === "pending" || colDef.key === "payment_rejected")
              ? (item.latest_order_amount ?? 0)
              : (item.total_paid_lifetime ?? item.total_amount)
          )}
        </div>
      </div>

      {/* Plan badge + Customer type */}
      <div
        style={{
          display: "flex",
          gap: "6px",
          flexWrap: "wrap",
          marginBottom: "10px",
        }}
      >
        <span
          style={{
            background: colDef.bg,
            color: colDef.color,
            borderRadius: "6px",
            padding: "2px 8px",
            fontSize: "11px",
            fontWeight: 700,
          }}
        >
          {planMap[item.plan_id] || item.plan_name || "N/A"}
        </span>
        <span
          style={{
            background: "#f5f5f5",
            color: "#6b7280",
            borderRadius: "6px",
            padding: "2px 8px",
            fontSize: "11px",
            fontWeight: 600,
          }}
        >
          {item.is_attorney === 1 ? "Attorney/IP" : "Corporate"}
        </span>
        {daysInfo && (
          <span
            style={{
              background: "transparent",
              color: daysInfo.color,
              fontSize: "11px",
              fontWeight: 700,
              marginLeft: "auto",
            }}
          >
            {daysInfo.text}
          </span>
        )}
      </div>

      {/* Divider */}
      <div style={{ height: "1px", background: "#f3f4f6", margin: "10px 0" }} />

      {/* Footer: Quick Actions */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "6px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "3px",
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* Primary number row */}
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <div
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: "#16a34a",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#111827",
                letterSpacing: "0.3px",
              }}
            >
              {item.mobile || (
                <span
                  style={{
                    fontWeight: 400,
                    color: "#9ca3af",
                    fontStyle: "italic",
                    fontSize: "11px",
                  }}
                >
                  No mobile
                </span>
              )}
            </span>
            {item.mobile &&
              (item.alternate_number ? (
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: [
                      {
                        key: "primary",
                        label: (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "2px 0",
                            }}
                          >
                            <div
                              style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "6px",
                                background: "#dcfce7",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <PhoneOutlined
                                style={{ color: "#16a34a", fontSize: "11px" }}
                              />
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  fontWeight: 500,
                                }}
                              >
                                Primary
                              </div>
                              <div
                                style={{
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: "#111827",
                                }}
                              >
                                {item.mobile}
                              </div>
                            </div>
                          </div>
                        ),
                      },
                      {
                        key: "alternate",
                        label: (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "2px 0",
                            }}
                          >
                            <div
                              style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "6px",
                                background: "#dbeafe",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <PhoneOutlined
                                style={{ color: "#2563eb", fontSize: "11px" }}
                              />
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  fontWeight: 500,
                                }}
                              >
                                Alternate
                              </div>
                              <div
                                style={{
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: "#111827",
                                }}
                              >
                                {item.alternate_number}
                              </div>
                            </div>
                          </div>
                        ),
                      },
                    ],
                    onClick: ({ key }) => {
                      const num =
                        key === "primary" ? item.mobile : item.alternate_number;
                      onCallLog({
                        phone: num,
                        name: item.customer_name,
                        id: item.user_id,
                      });
                    },
                  }}
                >
                  <button
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "5px",
                      background: "#dcfce7",
                      border: "none",
                      color: "#16a34a",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      fontSize: "11px",
                      flexShrink: 0,
                    }}
                  >
                    <PhoneOutlined />
                  </button>
                </Dropdown>
              ) : (
                <Tooltip title="Log a Call">
                  <button
                    onClick={() =>
                      onCallLog({
                        phone: item.mobile,
                        name: item.customer_name,
                        id: item.user_id,
                      })
                    }
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "5px",
                      background: "#dcfce7",
                      border: "none",
                      color: "#16a34a",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      fontSize: "11px",
                      flexShrink: 0,
                    }}
                  >
                    <PhoneOutlined />
                  </button>
                </Tooltip>
              ))}
          </div>
          {/* Alternate number row */}
          {item.alternate_number && (
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <div
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background: "#2563eb",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#374151",
                  letterSpacing: "0.3px",
                }}
              >
                {item.alternate_number}
              </span>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: "3px", flexShrink: 0 }}>
          {canEdit &&
            (isPaid ? (
              <Tooltip title="Upgrade from profile">
                <span style={disabledIconStyle}>
                  <EditOutlined />
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Upgrade Subscription">
                <Link
                  to={`/cms/manage-customer/edit-order/${item.latest_order_id || item.order_id}/${item.user_id}`}
                  style={{
                    ...iconBtnStyle,
                    background: "#eff6ff",
                    color: "#2563eb",
                    borderColor: "#bfdbfe",
                    textDecoration: "none",
                  }}
                >
                  <EditOutlined />
                </Link>
              </Tooltip>
            ))}

          {canDelete && (
            <Tooltip title="Delete">
              <button
                disabled={isPaid}
                onClick={() => !isPaid && onDelete(item.user_id, item.latest_order_id || item.order_id)}
                style={{
                  ...iconBtnStyle,
                  background: isPaid ? "#f5f5f5" : "#fef2f2",
                  color: isPaid ? "#bfbfbf" : "#dc2626",
                  borderColor: isPaid ? "#e5e5e5" : "#fecaca",
                  cursor: isPaid ? "not-allowed" : "pointer",
                }}
              >
                <DeleteOutlined />
              </button>
            </Tooltip>
          )}

          {colDef.key !== "expiring_soon" &&
            colDef.key !== "expired" &&
            (isFree || isPaid ? (
              <Tooltip
                title={isFree ? "Not available for Free Trial" : "Already paid"}
              >
                <span style={disabledIconStyle}>
                  <LinkOutlined />
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Payment Link">
                <button
                  onClick={() => onPaymentModal(item)}
                  style={{
                    ...iconBtnStyle,
                    background: "#f0fdf4",
                    color: "#16a34a",
                    borderColor: "#bbf7d0",
                  }}
                >
                  <LinkOutlined />
                </button>
              </Tooltip>
            ))}

          {item.invoice_url && isPaid && (
            <Tooltip title="Download Invoice">
              <a
                href={item.invoice_url}
                download={`Invoice_${item.order_id}.pdf`}
                style={{
                  ...iconBtnStyle,
                  background: "#f0fdf4",
                  color: "#16a34a",
                  borderColor: "#bbf7d0",
                  textDecoration: "none",
                }}
              >
                <DownloadOutlined />
              </a>
            </Tooltip>
          )}

          {colDef.key !== "paid" && (
            <Tooltip title="Communications">
              <button
                onClick={() =>
                  navigate(`/cms/inbox?userId=${item.user_id}`)
                }
                style={{
                  ...iconBtnStyle,
                  background: "#f0f4ff",
                  color: "#4f46e5",
                  borderColor: "#c7d2fe",
                }}
              >
                <CommentOutlined />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// SubscriptionKanban (main export)
//
// Props:
//   data      — array of orders
//   onRefresh — callback to re-fetch after delete
//   onViewAll — (columnKey) => void — when "View All" clicked
// ═══════════════════════════════════════════════════════════════════════════
const SubscriptionKanban = ({
  data = [],
  onRefresh,
  onViewAll,
  planMap = {},
}) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // ── Call log modal state ──
  const [callLogModal, setCallLogModal] = useState(null);
  const [callOutcome, setCallOutcome] = useState("Connected");
  const [callNote, setCallNote] = useState("");
  const [callDurationME, setCallDurationME] = useState("");
  const [callFuStatusME, setCallFuStatusME] = useState("");
  const [callNextDateME, setCallNextDateME] = useState("");
  const [callNextTimeME, setCallNextTimeME] = useState("");

  const addCustomerFollowUp = useAddCustomerFollowUp();
  const { data: followUpOptions = [] } = useFollowUpOptions();

  const handleClickToCall = (clientNo, clientName) => {
    // agent_no = the logged-in user's own mobile; the helper also
    // reports an unregistered number instead of failing silently.
    placeClickToCall(clientNo, clientName);
  };

  const resetCallLog = () => {
    setCallLogModal(null);
    setCallNote("");
    setCallOutcome("Connected");
    setCallDurationME("");
    setCallFuStatusME("");
    setCallNextDateME("");
    setCallNextTimeME("");
  };

  const handleLogCallSubmit = () => {
    addCustomerFollowUp.mutate(
      {
        customer_id: callLogModal.id,
        followup_type: "call",
        followup_date: moment().format("YYYY-MM-DD HH:mm:ss"),
        enquiry_status: callFuStatusME,
        direction: "outbound",
        delivery_status: "delivered",
        call_outcome: callOutcome,
        call_duration: callDurationME || null,
        content: callNote || `Call: ${callOutcome}`,
        remarks: callNote || null,
        subject: `Call — ${callOutcome}`,
        recipient: callLogModal.phone,
        created_by: Number(localStorage.getItem("cms_user_id")) || 1,
        ...(callNextDateME
          ? {
              next_followup_date: callNextDateME,
              ...(callNextTimeME ? { next_followup_time: callNextTimeME } : {}),
            }
          : {}),
      },
      { onSuccess: resetCallLog },
    );
  };

  const columnData = useMemo(() => {
    const buckets = {};
    COLUMNS.forEach((col) => (buckets[col.key] = []));
    data.forEach((item) => {
      const stage = classifyOrder(item);
      if (buckets[stage]) buckets[stage].push(item);
    });
    return buckets;
  }, [data]);

  const columnTotals = useMemo(() => {
    const totals = {};
    COLUMNS.forEach((col) => {
      if (col.key === "free_trial") { totals[col.key] = null; return; }
      totals[col.key] = (columnData[col.key] || []).reduce(
        (sum, item) => sum + parseFloat(
          (col.key === "pending" || col.key === "payment_rejected")
            ? (item.latest_order_amount ?? 0)
            : (item.total_paid_lifetime ?? 0)
        ),
        0,
      );
    });
    return totals;
  }, [columnData]);

  const openPaymentModal = (order) => {
    setSelectedOrder(order);
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedOrder(null);
    setLinkCopied(false);
  };

  const copyPaymentLink = () => {
    if (!selectedOrder?.payment_link) return;
    navigator.clipboard.writeText(selectedOrder.payment_link);
    setLinkCopied(true);
    toast.success("Payment link copied!");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleDelete = (userId, orderId) => {
    Modal.confirm({
      title: "Delete Subscription",
      icon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
      content: "This action is permanent and cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        try {
          const response = await deleteOrder({
            user_id: userId,
            order_id: orderId,
          });
          if (response.success) {
            toast.success(response.message || "Deleted successfully");
            onRefresh?.();
          } else {
            toast.warning(response.message || "Failed to delete");
          }
        } catch {
          toast.error("Failed to delete subscription.");
        }
      },
    });
  };

  return (
    <>
      {/* Kanban Board */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          overflowX: "auto",
          padding: "4px 0 16px",
          minHeight: "calc(100vh - 380px)",
        }}
      >
        {COLUMNS.map((col) => {
          const items = columnData[col.key] || [];
          const total = columnTotals[col.key] || 0;

          return (
            <div
              key={col.key}
              style={{
                flex: "0 0 280px",
                minWidth: "280px",
                background: "#f9fafb",
                borderRadius: "14px",
                border: `1px solid ${col.border}`,
                display: "flex",
                flexDirection: "column",
                maxHeight: "calc(100vh - 380px)",
              }}
            >
              {/* Column Header */}
              <div
                style={{
                  padding: "14px 16px",
                  borderBottom: `2px solid ${col.border}`,
                  background: col.bg,
                  borderRadius: "14px 14px 0 0",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "6px",
                    gap: "6px",
                    flexWrap: "nowrap",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        color: col.color,
                        fontSize: "15px",
                        flexShrink: 0,
                      }}
                    >
                      {col.icon}
                    </span>
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: "13px",
                        color: col.color,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col.title}
                    </span>
                    <span
                      style={{
                        background: col.color,
                        color: "white",
                        borderRadius: "12px",
                        padding: "1px 8px",
                        fontSize: "12px",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {items.length}
                    </span>
                  </div>
                  {onViewAll && (
                    <button
                      onClick={() => onViewAll(col.key)}
                      style={{
                        background: `${col.color}18`,
                        border: `1px solid ${col.color}40`,
                        color: col.color,
                        cursor: "pointer",
                        fontSize: 11,
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "3px 8px",
                        borderRadius: 7,
                        transition: "all 0.15s",
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${col.color}28`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = `${col.color}18`;
                      }}
                    >
                      View All <BarsOutlined />
                    </button>
                  )}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: col.color,
                    fontWeight: 600,
                    opacity: 0.8,
                  }}
                >
                  {col.key !== "free_trial" ? `₹${fmt(total)} total` : `${items.length} ${items.length === 1 ? "user" : "users"}`}
                </div>
              </div>

              {/* Column Body */}
              <div style={{ flex: 1, overflowY: "auto", padding: "12px 10px" }}>
                {items.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "32px 16px",
                      color: "#9ca3af",
                      fontSize: "13px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "28px",
                        marginBottom: "8px",
                        opacity: 0.4,
                      }}
                    >
                      {col.icon}
                    </div>
                    No subscriptions
                  </div>
                ) : (
                  items.map((item) => (
                    <KanbanCard
                      key={item.latest_order_id || item.order_id || `user-${item.user_id}`}
                      item={item}
                      colDef={col}
                      onPaymentModal={openPaymentModal}
                      onDelete={handleDelete}
                      planMap={planMap}
                      onCallLog={(info) => {
                        handleClickToCall(info.phone, info.name);
                        setCallOutcome("Connected");
                        setCallNote("");
                        setCallDurationME("");
                        setCallFuStatusME("");
                        setCallNextDateME("");
                        setCallNextTimeME("");
                        setCallLogModal(info);
                      }}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Call Log Modal ── */}
      <style>{`@keyframes meCallModalIn{from{opacity:0;transform:scale(.92) translateY(14px)}to{opacity:1;transform:scale(1) translateY(0)}}`}</style>
      {callLogModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1100,
            background: "rgba(15,23,42,.45)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "15px",
              width: "100%",
              maxWidth: "600px",
              maxHeight: "90vh",
              border: "2px solid #fde68a",
              overflow: "hidden",
              boxShadow: "0 5px 24px rgba(245,158,11,.1)",
              animation: "meCallModalIn .25s ease",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg,#f59e0b,#d97706)",
                padding: "11px 18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "9px" }}
              >
                <div
                  style={{
                    width: "30px",
                    height: "30px",
                    background: "rgba(255,255,255,.2)",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <PhoneOutlined />
                </div>
                <div>
                  <span
                    style={{
                      color: "white",
                      fontWeight: 700,
                      fontSize: "14px",
                    }}
                  >
                    Log a Call
                  </span>
                  <div
                    style={{
                      color: "rgba(255,255,255,.75)",
                      fontSize: "11px",
                      marginTop: "1px",
                    }}
                  >
                    {callLogModal.name} ({callLogModal.phone})
                  </div>
                </div>
              </div>
              <button
                onClick={resetCallLog}
                style={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,.2)",
                  border: "none",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>
            <div
              style={{
                padding: "16px 18px",
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "11px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "11px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#6b7280",
                        display: "block",
                        marginBottom: "5px",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                      }}
                    >
                      Call Outcome
                    </label>
                    <select
                      value={callOutcome}
                      onChange={(e) => setCallOutcome(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "9px 12px",
                        border: "1.5px solid #fde68a",
                        borderRadius: "9px",
                        fontSize: "13px",
                        outline: "none",
                        background: "white",
                        cursor: "pointer",
                        boxSizing: "border-box",
                      }}
                    >
                      {[
                        "Connected",
                        "No Answer",
                        "Voicemail Left",
                        "Call Back Later",
                        "Wrong Number",
                      ].map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#6b7280",
                        display: "block",
                        marginBottom: "5px",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                      }}
                    >
                      Call Duration
                    </label>
                    <input
                      value={callDurationME}
                      onChange={(e) => setCallDurationME(e.target.value)}
                      placeholder="e.g. 15 mins"
                      style={{
                        width: "100%",
                        padding: "9px 12px",
                        border: "1.5px solid #fde68a",
                        borderRadius: "9px",
                        fontSize: "13px",
                        outline: "none",
                        background: "white",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#6b7280",
                      display: "block",
                      marginBottom: "5px",
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                    }}
                  >
                    Call Notes
                  </label>
                  <textarea
                    placeholder="Add notes..."
                    value={callNote}
                    onChange={(e) => setCallNote(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1.5px solid #fde68a",
                      borderRadius: "9px",
                      fontSize: "13px",
                      outline: "none",
                      resize: "vertical",
                      minHeight: "80px",
                      background: "white",
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                      lineHeight: 1.6,
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#6b7280",
                      display: "block",
                      marginBottom: "5px",
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                    }}
                  >
                    Follow-up Status
                  </label>
                  <FollowUpStatusSelectME
                    value={callFuStatusME}
                    onChange={setCallFuStatusME}
                    followUpOptionsList={followUpOptions}
                  />
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "11px",
                    marginTop: "4px",
                    paddingTop: "10px",
                    borderTop: "1px dashed #fde68a",
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: "10px",
                        fontWeight: 700,
                        color: "#9ca3af",
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                        marginBottom: "3px",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                      }}
                    >
                      <CalendarOutlined style={{ fontSize: "11px" }} /> Next
                      Follow-up
                    </label>
                    <input
                      type="date"
                      value={callNextDateME}
                      onChange={(e) => setCallNextDateME(e.target.value)}
                      onClick={(e) => e.target.showPicker?.()}
                      style={{
                        width: "100%",
                        padding: "7px 10px",
                        border: "1.5px solid #e2e8f0",
                        borderRadius: "7px",
                        fontSize: "12px",
                        outline: "none",
                        background: "white",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: "10px",
                        fontWeight: 700,
                        color: "#9ca3af",
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                        marginBottom: "3px",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                      }}
                    >
                      Time
                    </label>
                    <input
                      type="time"
                      value={callNextTimeME}
                      onChange={(e) => setCallNextTimeME(e.target.value)}
                      onClick={(e) => e.target.showPicker?.()}
                      style={{
                        width: "100%",
                        padding: "7px 10px",
                        border: "1.5px solid #e2e8f0",
                        borderRadius: "7px",
                        fontSize: "12px",
                        outline: "none",
                        background: "white",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                    marginTop: "6px",
                  }}
                >
                  <button
                    onClick={resetCallLog}
                    style={{
                      padding: "8px 18px",
                      background: "#f1f5f9",
                      color: "#6b7280",
                      border: "1.5px solid #e2e8f0",
                      borderRadius: "10px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    disabled={addCustomerFollowUp.isPending}
                    onClick={handleLogCallSubmit}
                    style={{
                      padding: "8px 18px",
                      background: "linear-gradient(135deg,#f59e0b,#d97706)",
                      color: "white",
                      border: "none",
                      borderRadius: "10px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: addCustomerFollowUp.isPending
                        ? "not-allowed"
                        : "pointer",
                      boxShadow: "0 4px 12px rgba(245,158,11,.3)",
                      opacity: addCustomerFollowUp.isPending ? 0.7 : 1,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "7px",
                    }}
                  >
                    {addCustomerFollowUp.isPending ? (
                      <span className="spinner-border spinner-border-sm text-white" />
                    ) : (
                      <>
                        <CheckCircleOutlined style={{ fontSize: "12px" }} /> Log
                        Call
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedOrder && (
        <Modal
          open={showPaymentModal}
          onCancel={closePaymentModal}
          footer={null}
          centered
          width={720}
          destroyOnClose
        >
          {(() => {
            const row = selectedOrder;
            const hasLink = !!row.payment_link;
            const payMsg = hasLink
              ? `Hi ${row.customer_name}, please complete your Entermark subscription payment here: ${row.payment_link}`
              : "";
            const digitsOnly = (num) => (num || "").replace(/\D/g, "");

            const guardSend = (fn) => () => {
              if (!hasLink) { toast.warning("No payment link to send"); return; }
              fn();
            };

            return (
              <>
                <div className="mb-4">
                  <h5 className="fw-bold mb-1 d-flex align-items-center">
                    <i className="fa fa-credit-card me-2 text-success" />
                    Payment Details
                  </h5>
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    Share the payment link with the customer
                  </span>
                </div>

                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="text-muted" style={{ fontSize: 12 }}>Order ID</div>
                    <div className="fw-semibold fs-6">#{row.latest_order_id}</div>
                  </div>
                  <div className="col-md-6 text-md-end">
                    <div className="text-muted" style={{ fontSize: 12 }}>Created At</div>
                    <div className="fw-semibold fs-6">
                      {row.latest_order_created_at || "—"}
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="fw-semibold mb-1">Payment Link</div>
                  <Input
                    value={row.payment_link || ""}
                    readOnly
                    size="large"
                    placeholder="No active payment link"
                    addonAfter={
                      linkCopied ? (
                        <CheckOutlined style={{ color: "#52c41a", fontSize: 16 }} />
                      ) : (
                        <CopyOutlined
                          style={{
                            fontSize: 16,
                            cursor: hasLink ? "pointer" : "not-allowed",
                            color: hasLink ? undefined : "#bfbfbf",
                          }}
                          onClick={hasLink ? copyPaymentLink : undefined}
                        />
                      )
                    }
                  />
                  {!hasLink && (
                    <small style={{ color: "#f59e0b" }}>
                      No active payment link — customer may have already paid or this is not an online order.
                    </small>
                  )}
                  {hasLink && (
                    <small className="text-muted">
                      Share this link with the customer to complete payment
                    </small>
                  )}
                </div>

                <div>
                  <div className="fw-semibold mb-2">Send Payment Link</div>
                  <div className="d-flex gap-3 flex-wrap">
                    <Button
                      size="large"
                      disabled={!hasLink}
                      style={{ borderColor: "#1677ff", color: hasLink ? "#1677ff" : undefined }}
                      icon={<MailOutlined />}
                      onClick={guardSend(() =>
                        window.location.href = `mailto:${row.customer_email}?subject=${encodeURIComponent("Complete your Entermark subscription payment")}&body=${encodeURIComponent(payMsg)}`
                      )}
                    >
                      Email
                    </Button>
                    <Button
                      size="large"
                      disabled={!hasLink}
                      style={{ borderColor: "#25D366", color: hasLink ? "#25D366" : undefined }}
                      icon={<WhatsAppOutlined />}
                      onClick={guardSend(() =>
                        window.open(
                          `https://wa.me/${digitsOnly(row.mobile)}?text=${encodeURIComponent(payMsg)}`,
                          "_blank",
                        )
                      )}
                    >
                      WhatsApp
                    </Button>
                    <Button
                      size="large"
                      disabled={!hasLink}
                      style={{ borderColor: "#6c757d", color: hasLink ? "#6c757d" : undefined }}
                      icon={<MessageOutlined />}
                      onClick={guardSend(() =>
                        window.location.href = `sms:${row.mobile}?&body=${encodeURIComponent(payMsg)}`
                      )}
                    >
                      SMS
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </Modal>
      )}
    </>
  );
};

export { COLUMNS, classifyOrder };
export default SubscriptionKanban;
